import { usePatientNumericObservationsByValueSet } from '@ctw/shared/api/fhir/observations';
import { Button } from '@ctw/shared/components/button';
import { MultiLineChart } from '@ctw/shared/components/charts/multi-line-chart';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { FilterBar } from '@ctw/shared/components/filter-bar/filter-bar';
import type { FilterChangeEvent } from '@ctw/shared/components/filter-bar/filter-bar-types';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { LegacyTable } from '@ctw/shared/components/tables/legacy-table';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { HotloadGate } from '@ctw/shared/content/hotload-gate';
import { defaultFilters, filters } from '@ctw/shared/content/trends/filters';
import { getDataAndColumnsForNumericObservationsByValueSet } from '@ctw/shared/content/trends/helpers';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { type ValueSetKey, ValueSets } from '@ctw/shared/utils/value-sets';
import { useState } from 'react';

type ViewMode = 'chart' | 'table';

export const PatientTrends = withErrorBoundary({
  boundaryName: 'PatientTrends',
  Component: () => {
    const { patient } = usePatientContext();
    const [selectedFilters, setSelectedFilters] = useState<FilterChangeEvent>(defaultFilters);
    const [viewMode, setViewMode] = useState<ViewMode>('chart');
    const [useTimeScale, setUseTimeScale] = useState<boolean>(true);

    const selectedValueSets = Array.isArray(selectedFilters.loincCode?.selected)
      ? selectedFilters.loincCode.selected.map((vs) => vs as ValueSetKey)
      : [];

    const handleFilterChange = (updatedFilters: FilterChangeEvent) => {
      setSelectedFilters(updatedFilters);
    };

    const hasObservationFilterSelected =
      selectedFilters.loincCode?.selected &&
      Array.isArray(selectedFilters.loincCode?.selected) &&
      selectedFilters.loincCode?.selected.length > 0;

    // Registered unconditionally so the filter picker and view-mode toggle stay interactive
    // while the data branch below is gated on hotload readiness — picking a filter doesn't
    // depend on hotstorage and shouldn't wait on it.
    useMainContentHeader(
      'PatientTrends',
      () => (
        <div className={tw`flex w-full justify-between`}>
          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            selected={selectedFilters}
            defaultFilters={defaultFilters}
          />
          <div className={tw`ml-auto flex gap-2`}>
            {viewMode === 'chart' && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setUseTimeScale(!useTimeScale)}
                className={tw`rounded-md`}
                testId="trends-time-scale-toggle"
              >
                {`Time Scale: ${useTimeScale ? 'On' : 'Off'}`}
              </Button>
            )}
            <div className={tw`inline-flex rounded-md shadow-sm`}>
              <Button
                type="button"
                variant={viewMode === 'chart' ? 'primary' : 'secondary'}
                onClick={() => setViewMode('chart')}
                className={tw`rounded-r-none`}
                testId="trends-view-mode-chart"
              >
                Chart
              </Button>
              <Button
                type="button"
                variant={viewMode === 'table' ? 'primary' : 'secondary'}
                onClick={() => setViewMode('table')}
                className={tw`rounded-l-none`}
                testId="trends-view-mode-table"
              >
                Table
              </Button>
            </div>
          </div>
        </div>
      ),
      [selectedFilters, defaultFilters, filters, viewMode, useTimeScale],
    );

    if (!hasObservationFilterSelected) {
      return (
        <div className={tw`py-8 text-center`}>
          Please select an observation filter to view trends.
        </div>
      );
    }

    return (
      <HotloadGate upid={patient.UPID} resourceTypes={['Observation']}>
        <PatientTrendsContent
          selectedValueSets={selectedValueSets}
          viewMode={viewMode}
          useTimeScale={useTimeScale}
        />
      </HotloadGate>
    );
  },
});

interface PatientTrendsContentProps {
  selectedValueSets: Array<ValueSetKey>;
  viewMode: ViewMode;
  useTimeScale: boolean;
}

const PatientTrendsContent = ({
  selectedValueSets,
  viewMode,
  useTimeScale,
}: PatientTrendsContentProps) => {
  const observations = usePatientNumericObservationsByValueSet(selectedValueSets);

  const { tableData, tableColumns, chartData } = getDataAndColumnsForNumericObservationsByValueSet(
    observations.data,
  );

  if (observations.isLoading) {
    return <LoadingSpinner centered={true} message="Loading trends..." />;
  }

  // Determine if we have no data at all or just no filtered data
  const isEmptyQuery = !observations.data || observations.data.length === 0;
  const hasZeroFilteredRecords = !isEmptyQuery && tableData.length === 0;

  if (tableData.length === 0) {
    return (
      <EmptyPatientTable
        hasZeroFilteredRecords={hasZeroFilteredRecords}
        resourceName="observations"
      />
    );
  }

  const chartTitle =
    chartData.length > 0
      ? selectedValueSets.map((vs) => ValueSets[vs]?.display).join(', ')
      : 'Patient Observations';

  return (
    <div className={tw`flex h-full w-full flex-col gap-6`}>
      {viewMode === 'chart' ? (
        <MultiLineChart
          data={chartData}
          title={chartTitle}
          height="90%"
          testId="trends-chart"
          useTimeScale={useTimeScale}
        />
      ) : (
        <div>
          <LegacyTable
            title="Patient Observations"
            titleVisuallyHidden={false}
            columns={tableColumns}
            records={tableData}
            emptyMessage="No data available"
            spacing="tight"
            testId="trends-table"
          />
        </div>
      )}
    </div>
  );
};
